import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';

function resolveMediaUrl(path) {
  if (!path) return null;
  return path.startsWith('http') ? path : `${BACKEND_URL}/storage/${path}`;
}

function getInitials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
}

function JobRow({ job }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/jobs/${job.id}`)}
      className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-primary-blue transition"
    >
      <p className="text-base font-semibold text-gray-800">{job.title}</p>
      <p className="text-sm text-gray-500 mt-0.5">{job.location || 'Location not specified'}</p>

      <div className="flex flex-wrap gap-1.5 mt-3">
        {job.job_type && (
          <span className="bg-secondary-green text-primary-green text-xs font-medium px-2.5 py-1 rounded-full">
            {job.job_type.replace(/_/g, ' ')}
          </span>
        )}
        {job.work_mode && (
          <span className="bg-secondary-indigo text-primary-blue text-xs font-medium px-2.5 py-1 rounded-full">
            {job.work_mode.replace(/_/g, ' ')}
          </span>
        )}
        {job.experience_required != null && (
          <span className="bg-secondary-orange text-primary-orange text-xs font-medium px-2.5 py-1 rounded-full">
            {job.experience_required}+ yrs
          </span>
        )}
      </div>

      {(job.salary_min || job.salary_max) && (
        <p className="text-xs text-gray-500 mt-2">
          💰 ₹{Number(job.salary_min).toLocaleString()} - ₹{Number(job.salary_max).toLocaleString()}
        </p>
      )}
      {job.openings != null && (
        <p className="text-xs text-gray-400 mt-1">👥 {job.openings} opening{job.openings === 1 ? '' : 's'}</p>
      )}
    </button>
  );
}

function CompanyPublicProfile() {
  const { slug } = useParams();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/companies/${slug}`)
      .then((res) => setCompany(res.data.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="p-10 text-center text-gray-500">Loading...</div>;
  if (notFound || !company) return <div className="p-10 text-center text-gray-500">Company not found.</div>;

  const logoSrc = resolveMediaUrl(company.company_logo);
  const coverSrc = resolveMediaUrl(company.company_cover_banner);

  return (
    <div className="min-h-screen bg-secondary-blue">
      <Navbar user={user} />

      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-6 sm:py-10">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Cover banner */}
          <div className="w-full h-32 sm:h-40 bg-gray-100">
            {coverSrc && (
              <img src={coverSrc} alt="" className="w-full h-full object-cover" />
            )}
          </div>

          <div className="p-4 sm:p-8">
            {/* Logo + name row */}
            <div className="flex items-start gap-4 -mt-12 sm:-mt-16">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt=""
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover border-4 border-white bg-white shadow"
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-blue-500 border-4 border-white shadow flex items-center justify-center font-bold text-white text-2xl">
                  {getInitials(company.name)}
                </div>
              )}

              <div className="flex-1 min-w-0 pt-12 sm:pt-16">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{company.name}</h1>
                  {company.company_verified_status === 'verified' && (
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-secondary-green text-primary-green">
                      ✓ Verified
                    </span>
                  )}
                </div>
                {company.company_industry && (
                  <p className="text-sm text-gray-500 mt-0.5">{company.company_industry}</p>
                )}
              </div>
            </div>

            {/* Meta row */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-xs text-gray-500">
              {company.company_headquarters && <span>📍 {company.company_headquarters}</span>}
              {company.company_size && <span>👥 {company.company_size} employees</span>}
              {company.company_founded_year && <span>📅 Founded {company.company_founded_year}</span>}
              {company.company_website && (
                <a href={company.company_website} target="_blank" rel="noreferrer" className="text-primary-blue hover:underline">
                  {company.company_website}
                </a>
              )}
            </div>

            {/* About */}
            {company.company_about && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">About {company.name}</p>
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{company.company_about}</p>
              </div>
            )}

            {/* Culture */}
            {company.company_culture && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Culture</p>
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{company.company_culture}</p>
              </div>
            )}

            {/* Benefits */}
            {company.company_benefits?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Benefits</p>
                <div className="flex flex-wrap gap-2">
                  {company.company_benefits.map((b) => (
                    <span key={b} className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Social links */}
            {(company.company_linkedin || company.company_facebook || company.company_instagram) && (
              <div className="mt-4 flex gap-3 text-xs">
                {company.company_linkedin && (
                  <a href={company.company_linkedin} target="_blank" rel="noreferrer" className="text-primary-blue hover:underline">LinkedIn</a>
                )}
                {company.company_facebook && (
                  <a href={company.company_facebook} target="_blank" rel="noreferrer" className="text-primary-blue hover:underline">Facebook</a>
                )}
                {company.company_instagram && (
                  <a href={company.company_instagram} target="_blank" rel="noreferrer" className="text-primary-blue hover:underline">Instagram</a>
                )}
              </div>
            )}

            {/* Jobs */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <h3 className="font-semibold text-primary-dark mb-3">
                Open positions ({company.jobs?.length || 0})
              </h3>
              {company.jobs?.length > 0 ? (
                <div className="space-y-2">
                  {company.jobs.map((job) => <JobRow key={job.id} job={job} />)}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No open positions right now.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyPublicProfile;